import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";
import * as APIModels from "../../../../models";
import { SubmissionService } from "../../../../services/SubmissionService";

type ItemizedSubmission = {
    key: string;
    countyID: string;
    divisionID: string;
    caseNumber: string;
};

interface CheckSimilarModalProps {
    submission: APIModels.Submission;
    isOpen: boolean;
    handleClose: () => void;
    currentCounty?: string;
}

export default function CheckSimilarModal(params: CheckSimilarModalProps) {
    const { submission, isOpen, handleClose, currentCounty } = params;
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [itemizedSubmission, setItemizedSubmission] = useState<
        ItemizedSubmission[]
    >([]);
    const [checkSimilarRequest, setCheckSimilarRequest] =
        useState<APIModels.CheckSimilar>({
            attorneyID: submission.attorneyID,
            countyID: currentCounty || submission.caseData?.county || "",
        } as APIModels.CheckSimilar);

    useEffect(() => {
        const county = currentCounty || submission.caseData?.county || "";
        console.log({
            "checking similar": county,
            "sumission county": submission.caseData?.county,
            "current county": currentCounty,
        });
        setCheckSimilarRequest({
            attorneyID: submission.attorneyID,
            countyID: county,
        } as APIModels.CheckSimilar);
    }, [currentCounty, submission.caseData?.county, submission.attorneyID]);

    const checkSimilar = async () => {
        const county = currentCounty || submission.caseData?.county;
        console.log("checking similar", county);
        setError("");
        setItemizedSubmission([]);
        setLoading(true);

        try {
            if (
                !county ||
                county === undefined ||
                county === null ||
                county === ""
            ) {
                setError(
                    "County is required make sure it is set before using this feature"
                );
                setLoading(false);
                return;
            }
            const resp = await SubmissionService.checkSimilar({
                body: {
                    attorneyID: submission.attorneyID,
                    countyID: county,
                },
            });

            if (!resp) {
                setError("Something went wrong communicating with the server");
                setLoading(false);
                return;
            }

            if (resp.successful && resp.data) {
                const itemizedSubmissionData: ItemizedSubmission[] =
                    resp.data.map((s) => {
                        return {
                            key: s.id ?? "",
                            countyID: s.caseData?.county ?? "",
                            divisionID: s.caseData?.division ?? "",
                            caseNumber: s.caseData?.courtCaseNumber ?? "",
                        };
                    });

                if (itemizedSubmissionData.length > 100) {
                    setError("Too many results. Truncating results to 100");
                    itemizedSubmissionData.splice(100);
                }
                setItemizedSubmission(itemizedSubmissionData);
            } else {
                setError(
                    resp.errorMessage ??
                        "Fatal Error occured. Please try again."
                );
            }
        } catch (error) {
            setError("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                component: "form",
                onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    await checkSimilar();
                },
            }}
            hideBackdrop
            maxWidth="lg"
        >
            <DialogTitle>Check Similar Cases already Filed</DialogTitle>
            <DialogContent>
                <Stack direction="row" hidden>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            id="efmeReciept"
                            aria-describedby="inputGroupPrepend"
                            type="checkbox"
                            checked={
                                checkSimilarRequest.divisionID ===
                                submission.caseData?.division
                            }
                            onChange={(event) => {
                                setCheckSimilarRequest({
                                    ...checkSimilarRequest,
                                    divisionID: event.target.checked
                                        ? submission.caseData?.division
                                        : undefined,
                                });
                            }}
                        />
                        <label htmlFor="efmeReciept">
                            Check Similar Divisions
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            id="efmeReciept"
                            aria-describedby="inputGroupPrepend"
                            type="checkbox"
                            checked={
                                checkSimilarRequest.caseTypeID ===
                                submission.caseData?.caseType
                            }
                            onChange={(event) => {
                                setCheckSimilarRequest({
                                    ...checkSimilarRequest,
                                    caseTypeID: event.target.checked
                                        ? submission.caseData?.caseType
                                        : undefined,
                                });
                            }}
                        />
                        <label htmlFor="efmeReciept">
                            Check Similar Counties
                        </label>
                    </div>
                </Stack>
                <table>
                    <thead>
                        <Stack
                            direction="row"
                            spacing={4}
                            width="100%"
                            justifyContent="space-between"
                            textAlign="left"
                        >
                            <th>County</th>
                            <th>Division</th>
                            <th>Case Number</th>
                        </Stack>
                    </thead>
                    <tbody>
                        {itemizedSubmission && itemizedSubmission.length > 0 ? (
                            itemizedSubmission.map((s) => {
                                return (
                                    <Stack
                                        direction="row"
                                        spacing={4}
                                        width="100%"
                                        justifyContent="space-between"
                                        textAlign="left"
                                        key={s.key}
                                    >
                                        <td>{s.countyID}</td>
                                        <td>{s.divisionID}</td>
                                        <td>{s.caseNumber}</td>
                                    </Stack>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={3}>No similar cases found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </DialogContent>
            <DialogActions>
                <Stack direction="row">
                    {error && <span style={{ color: "red" }}>{error}</span>}
                    <Button type="submit" loading={loading}>
                        Submit
                    </Button>
                    <Button onClick={handleClose}>Cancel</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
